import type { CSSProperties, ReactNode } from "react";

export interface CardProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  accent?: string;
}

export function Card({
  children,
  className = "",
  style,
  accent,
}: CardProps): ReactNode {
  return (
    <div
      style={{
        ...(accent ? { borderLeft: `3px solid ${accent}` } : {}),
        ...style,
      }}
      className={`overflow-hidden rounded-3 border border-n-150 bg-white shadow-1 ${className}`}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  pills?: ReactNode;
  right?: ReactNode;
  icon?: ReactNode;
}

export function CardHeader({
  title,
  subtitle,
  pills,
  right,
  icon,
}: CardHeaderProps): ReactNode {
  const hasSecondRow = pills || right;
  return (
    <div className="flex flex-col gap-1 border-b border-n-150 px-3.5 pt-2.5 pb-2 pr-9">
      <div className="flex items-center gap-1.5">
        {icon}
        <h2 className="m-0 text-[13.5px] font-semibold tracking-[-0.2px] text-n-900">
          {title}
        </h2>
      </div>
      {subtitle && (
        <div className="text-[11.5px] leading-[1.4] tracking-[-0.05px] text-n-500">
          {subtitle}
        </div>
      )}
      {hasSecondRow && (
        <div className="mt-0.5 flex min-h-[22px] items-center gap-1.5">
          {pills && (
            <div className="flex flex-wrap items-center gap-1.5">{pills}</div>
          )}
          <div className="min-w-[4px] flex-1" />
          {right && (
            <div className="flex flex-shrink-0 items-center gap-1">{right}</div>
          )}
        </div>
      )}
    </div>
  );
}

export interface TableHeaderProps {
  cols: string;
  children?: ReactNode;
}

export function TableHeader({ cols, children }: TableHeaderProps): ReactNode {
  return (
    <div
      className="border-b border-n-150 bg-n-50 px-3.5 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.4px] text-n-500"
      style={{ display: "grid", gridTemplateColumns: cols, gap: 10 }}
    >
      {children}
    </div>
  );
}
